import { Check, ExternalLink, Flag, RotateCcw, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  monthLabel,
  useReviewSignedContract,
  useSignedContractDetail,
  type ReviewStatus,
  type SignedContract,
  type SignedContractDetail,
  type SignedContractRef,
} from "../api";
import { hubspotDealUrl, pandadocUrl } from "../../contracts/links";
import { fmtCur2, fmtDate } from "../../renewals/format";

const num = (v: number | string | null | undefined): number =>
  v == null ? 0 : typeof v === "number" ? v : Number(v) || 0;

/** Review quick-view for one signed contract: the numbers under review, the
 *  verdict, and — expanded — the contract's full record from Cinch (line items,
 *  discounts, PandaDoc, HubSpot).
 *
 *  Both halves are live. The summary comes from the worklist's live read and
 *  the detail is fetched per-contract on open, so what the reviewer approves is
 *  what Cinch currently holds. */
export function SignedContractDrawer({
  period,
  contract,
  canAct,
  expanded,
  presetFlag,
  onExpandedChange,
  onClose,
}: {
  period: string;
  contract: SignedContract | null;
  canAct: boolean;
  expanded: boolean;
  /** True when opened via a row's Flag button — preselects the flagged verdict
   *  so the reviewer lands straight in the notes box. */
  presetFlag: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onClose: () => void;
}) {
  const review = useReviewSignedContract(period);
  const detail = useSignedContractDetail(period, contract?.id ?? null);
  const [status, setStatus] = useState<ReviewStatus>("pending");
  const [notes, setNotes] = useState("");
  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  // Seed the form when the target contract changes (not on every refetch).
  const seededKey = useRef<string | null>(null);
  useEffect(() => {
    if (contract && contract.id !== seededKey.current) {
      seededKey.current = contract.id;
      setStatus(presetFlag ? "flagged" : contract.review_status);
      setNotes(contract.review_notes ?? "");
      if (presetFlag) setTimeout(() => notesRef.current?.focus(), 150);
    }
    if (!contract) seededKey.current = null;
  }, [contract, presetFlag]);

  const dirty =
    !!contract &&
    (status !== contract.review_status ||
      (notes ?? "") !== (contract.review_notes ?? ""));

  function save() {
    if (!contract) return;
    review.mutate(
      { contractId: contract.id, status, notes },
      {
        onSuccess: (updated) =>
          toast.success(
            updated.review_status === "flagged"
              ? `${title(contract)} flagged`
              : updated.review_status === "approved"
                ? `${title(contract)} approved`
                : `${title(contract)} reset to pending`,
          ),
        onError: (e) =>
          toast.error("Couldn't save review", { description: (e as Error).message }),
      },
    );
  }

  const drift = contract ? driftedFields(contract, detail.data) : [];

  return (
    <EntityDrawer
      open={contract != null}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      title={contract ? title(contract) : ""}
      subtitle={
        contract
          ? `Signed ${fmtDate(contract.sign_date)} · ${monthLabel(period)} close`
          : undefined
      }
      onClose={onClose}
    >
      {contract && (
        <div className="flex flex-col gap-5 p-4">
          {/* The frozen facts under review */}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border px-4 py-3 sm:grid-cols-3">
            <Stat label="MRR">
              <span className="font-semibold">{fmtCur2(contract.mrr_total)}</span>
            </Stat>
            <Stat label="ARR">{fmtCur2((contract.mrr_total ?? 0) * 12)}</Stat>
            <Stat label="Contract type">{contract.contract_type_name ?? "—"}</Stat>
            <Stat label="Term">
              {contract.term_months ? `${contract.term_months} months` : "—"}
            </Stat>
            <Stat label="Payment">{contract.payment_type ?? "—"}</Stat>
            <Stat label="Closer">{contract.closer_name ?? "—"}</Stat>
            <Stat label="Sign date">{fmtDate(contract.sign_date)}</Stat>
            <Stat label="Billing effective">
              {contract.billing_effective_from ? (
                fmtDate(contract.billing_effective_from)
              ) : (
                <span
                  className="font-medium text-warning"
                  title="No billing effective date — this contract will surface on the billing-start worklist"
                >
                  missing
                </span>
              )}
            </Stat>
            <Stat label="DBA">{contract.company_dba ?? "—"}</Stat>
          </dl>

          {/* Where to go to check it */}
          <div className="flex flex-wrap gap-2">
            <SourceLink
              href={detail.data?.pandadoc_id ? pandadocUrl(detail.data.pandadoc_id) : null}
              label="PandaDoc"
              missing="No PandaDoc id on this contract"
            />
            <SourceLink
              href={
                detail.data?.hubspot_deal_id
                  ? hubspotDealUrl(detail.data.hubspot_deal_id)
                  : null
              }
              label="HubSpot deal"
              missing="No HubSpot deal linked"
            />
          </div>

          {/* Verdict */}
          <div className="space-y-3 rounded-md border px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Review
              </span>
              <StatusBadge value={contract.review_status} />
            </div>
            <div className="flex flex-wrap gap-2">
              <VerdictButton
                active={status === "approved"}
                activeClass="border-success bg-success-light text-success"
                onClick={() => setStatus("approved")}
                disabled={!canAct}
              >
                <Check className="h-3.5 w-3.5" /> Approve
              </VerdictButton>
              <VerdictButton
                active={status === "flagged"}
                activeClass="border-warning bg-warning-light text-warning"
                onClick={() => setStatus("flagged")}
                disabled={!canAct}
              >
                <Flag className="h-3.5 w-3.5" /> Flag
              </VerdictButton>
              <VerdictButton
                active={status === "pending"}
                activeClass="border-border bg-muted text-foreground"
                onClick={() => setStatus("pending")}
                disabled={!canAct}
              >
                <RotateCcw className="h-3.5 w-3.5" /> Clear
              </VerdictButton>
            </div>
            <Textarea
              ref={notesRef}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canAct}
              rows={3}
              aria-label="Review notes"
              placeholder={
                status === "flagged"
                  ? "What's wrong with this contract? (context for whoever fixes it)"
                  : "Optional notes…"
              }
            />
            {contract.reviewed_at && (
              <p className="text-xs text-muted-foreground">
                Last reviewed by {contract.reviewed_by_name ?? "—"} on{" "}
                {new Date(contract.reviewed_at).toLocaleString()}.
              </p>
            )}
            <div className="flex items-center gap-3">
              <Button onClick={save} disabled={!canAct || !dirty || review.isPending}>
                {review.isPending ? "Saving…" : "Save review"}
              </Button>
              {!canAct && (
                <span className="text-xs text-muted-foreground">
                  Only a RevOps or CRO owner can record a verdict.
                </span>
              )}
            </div>
          </div>

          {/* The contract itself */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Contract detail
              </span>
              <span className="text-[11px] text-muted-foreground">
                Live from Cinch
              </span>
            </div>

            {/* Both halves are live, so they normally agree. When they don't,
                the worklist read and the detail read happened either side of an
                edit — name the fields rather than let the reviewer wonder. */}
            {drift.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning-light/50 px-3 py-2 text-xs text-warning">
                <TriangleAlert className="mt-px h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  This contract changed while you were looking at it —{" "}
                  {drift.map((d, i) => (
                    <span key={d.label}>
                      {i > 0 && "; "}
                      <b>{d.label}</b>: {d.frozen} → {d.live}
                    </span>
                  ))}
                  . Reload to review the current version.
                </span>
              </div>
            )}

            {detail.isLoading && (
              <div className="h-24 animate-pulse rounded-md border bg-muted/30" />
            )}
            {detail.error && (
              <p className="rounded-md border border-dashed px-3 py-4 text-xs text-muted-foreground">
                Couldn't load the live contract from Cinch:{" "}
                {(detail.error as Error).message}
              </p>
            )}
            {detail.data && <ContractDetailBody detail={detail.data} expanded={expanded} />}
          </div>
        </div>
      )}
    </EntityDrawer>
  );
}

function ContractDetailBody({
  detail,
  expanded,
}: {
  detail: SignedContractDetail;
  expanded: boolean;
}) {
  const lineItems = detail.contract_line_items ?? [];
  const discounts = detail.contract_discounts ?? [];
  const subtotal = lineItems.reduce((s, li) => s + num(li.item_total), 0);
  const total = subtotal + num(detail.base_fee);
  const signer = [detail.first_name, detail.last_name].filter(Boolean).join(" ");
  const place = [detail.city, detail.state, detail.zip].filter(Boolean).join(", ");
  const lineage: { label: string; refs: SignedContractRef[] }[] = [
    { label: "Replaces", refs: one(detail.replacement_contract_details) },
    { label: "Replaced by", refs: detail.replaced_by_contracts ?? [] },
    { label: "Amends", refs: one(detail.amendment_contract_details) },
    { label: "Amended by", refs: detail.amended_by_contracts ?? [] },
    { label: "Reseller contract", refs: one(detail.reseller_contract_details) },
    { label: "Reseller for", refs: detail.reseller_by_contracts ?? [] },
  ].filter((g) => g.refs.length > 0);

  return (
    <div className="space-y-4">
      {/* A debooked contract on the signed list is itself the finding. */}
      {detail.debooked && (
        <div className="rounded-md border border-danger/40 bg-danger-light/50 px-3 py-2 text-xs text-danger">
          <b>Debooked.</b> {detail.debooked_reason || "No reason recorded."}
        </div>
      )}

      {expanded && (
        <>
          <Section title="Contract">
            <Grid>
              <Field label="Status">{detail.contract_status ?? "—"}</Field>
              <Field label="Account">{detail.account_name ?? "—"}</Field>
              <Field label="Company">{detail.company_name ?? "—"}</Field>
              <Field label="DBA">{detail.company_dba ?? "—"}</Field>
              {detail.assigned_company_name && (
                <Field label="Assigned company">{detail.assigned_company_name}</Field>
              )}
              <Field label="Billing from">{fmtDate(detail.billing_effective_from)}</Field>
              <Field label="Billing through">
                {detail.billing_effective_through
                  ? fmtDate(detail.billing_effective_through)
                  : "open"}
              </Field>
              <Field label="Renewal date">{fmtDate(detail.renewal_date)}</Field>
              {detail.next_renewal_when && (
                <Field label="Next renewal">{fmtDate(detail.next_renewal_when)}</Field>
              )}
              <Field label="Auto-renew">{yesNo(detail.is_auto_renew)}</Field>
              {detail.renewal_term && (
                <Field label="Renewal term">
                  {detail.renewal_term === "MONTH_TO_MONTH"
                    ? `Month-to-month${detail.mtm_uplift_pct ? ` · ${detail.mtm_uplift_pct}% uplift` : ""}`
                    : `${detail.renewal_term_months ?? detail.term_months ?? "—"} months`}
                </Field>
              )}
              {detail.notice_period_days != null && (
                <Field label="Notice period">{detail.notice_period_days} days</Field>
              )}
              <Field label="Lead source">{detail.lead_source ?? "—"}</Field>
            </Grid>
          </Section>

          <Section title="Contracted for">
            <Grid>
              <Field label="Initial contacts">
                {detail.initial_contacts != null
                  ? detail.initial_contacts.toLocaleString()
                  : "—"}
              </Field>
              <Field label="Per 10k more">
                {detail.ten_k_more != null ? fmtCur2(detail.ten_k_more) : "—"}
              </Field>
              <Field label="Billed on email count">{yesNo(detail.use_email_count)}</Field>
              <Field label="Custom terms">{yesNo(detail.custom_terms)}</Field>
            </Grid>
            {detail.additional_terms && (
              <p className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/30 px-3 py-2 text-sm">
                {detail.additional_terms}
              </p>
            )}
          </Section>

          {(signer || detail.email || place) && (
            <Section title="Signed by">
              <Grid>
                <Field label="Name">{signer || "—"}</Field>
                <Field label="Title">{detail.title ?? "—"}</Field>
                <Field label="Email">{detail.email ?? "—"}</Field>
                <Field label="Phone">{detail.phone ?? "—"}</Field>
                <Field label="Address">
                  {[detail.street, place].filter(Boolean).join(" · ") || "—"}
                </Field>
              </Grid>
            </Section>
          )}

          {/* Lineage is why a "new" contract might not be new revenue — the
              single most consequential thing to notice on this worklist. */}
          {lineage.length > 0 && (
            <Section title="Related contracts">
              <div className="space-y-2">
                {lineage.map((g) => (
                  <div key={g.label}>
                    <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      {g.label}
                    </dt>
                    {g.refs.map((r) => (
                      <dd key={r.id} className="text-sm">
                        {r.company_name ?? r.id.slice(0, 8)}
                        <span className="text-muted-foreground">
                          {r.contract_type_name ? ` · ${r.contract_type_name}` : ""}
                          {r.mrr_total != null ? ` · ${fmtCur2(r.mrr_total)}/mo` : ""}
                          {r.sign_date ? ` · signed ${fmtDate(r.sign_date)}` : ""}
                        </span>
                      </dd>
                    ))}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      )}

      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Item</th>
              <th className="px-3 py-2 text-right font-semibold">Qty</th>
              <th className="px-3 py-2 text-right font-semibold">Rate</th>
              {expanded && (
                <th className="px-3 py-2 text-right font-semibold">Overage</th>
              )}
              <th className="px-3 py-2 text-right font-semibold">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {lineItems.length === 0 ? (
              <tr>
                <td
                  colSpan={expanded ? 5 : 4}
                  className="px-3 py-4 text-center text-xs text-muted-foreground"
                >
                  No line items on this contract.
                </td>
              </tr>
            ) : (
              lineItems.map((li, i) => (
                <tr key={li.uuid ?? i}>
                  <td className="px-3 py-2">{lineName(li)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {/* Cinch sends qty as a decimal STRING ("70.000000"), so
                        coerce before formatting — toLocaleString on a string
                        is a no-op and renders the trailing zeroes. */}
                    {li.qty == null ? "—" : num(li.qty).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {li.price_per_unit == null ? "—" : fmtCur2(num(li.price_per_unit))}
                  </td>
                  {expanded && (
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {li.overage_price_per_unit == null
                        ? "—"
                        : fmtCur2(num(li.overage_price_per_unit))}
                    </td>
                  )}
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {fmtCur2(num(li.item_total))}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <dl className="space-y-1 text-sm">
        <Row label={`Line items subtotal (${lineItems.length})`} value={fmtCur2(subtotal)} />
        {num(detail.base_fee) !== 0 && (
          <Row label="Base fee" value={fmtCur2(num(detail.base_fee))} />
        )}
        <Row label="Total contract value" value={fmtCur2(total)} bold />
      </dl>

      {discounts.length > 0 && (
        <div className="space-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Discounts ({discounts.length})
          </span>
          <dl className="space-y-1 text-sm">
            {discounts.map((d, i) => (
              <Row
                key={i}
                label={
                  d.target_line_item
                    ? lineNameByUuid(lineItems, d.target_line_item)
                    : "Whole contract"
                }
                value={discountLabel(d)}
              />
            ))}
          </dl>
        </div>
      )}

      {expanded && detail.notes && (
        <div className="space-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Contract notes
          </span>
          <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm">{detail.notes}</p>
        </div>
      )}
    </div>
  );
}

/** Fields the reviewer approved that the live contract no longer agrees with.
 *  Named rather than a blanket "this may have changed" so a drifted contract
 *  reads as a finding instead of a disclaimer. */
export function driftedFields(
  frozen: SignedContract,
  live: SignedContractDetail | undefined,
): { label: string; frozen: string; live: string }[] {
  if (!live) return [];
  const out: { label: string; frozen: string; live: string }[] = [];
  if (live.mrr_total != null && num(live.mrr_total) !== num(frozen.mrr_total)) {
    out.push({
      label: "MRR",
      frozen: fmtCur2(frozen.mrr_total),
      live: fmtCur2(num(live.mrr_total)),
    });
  }
  if (live.sign_date != null && live.sign_date !== frozen.sign_date) {
    out.push({
      label: "Sign date",
      frozen: fmtDate(frozen.sign_date),
      live: fmtDate(live.sign_date),
    });
  }
  if (
    live.billing_effective_from !== undefined &&
    (live.billing_effective_from ?? null) !== (frozen.billing_effective_from ?? null)
  ) {
    out.push({
      label: "Billing effective",
      frozen: frozen.billing_effective_from ? fmtDate(frozen.billing_effective_from) : "missing",
      live: live.billing_effective_from ? fmtDate(live.billing_effective_from) : "missing",
    });
  }
  return out;
}

function title(c: SignedContract): string {
  return c.company_name ?? c.company_dba ?? `Contract ${c.id.slice(0, 8)}`;
}

function SourceLink({
  href,
  label,
  missing,
}: {
  href: string | null;
  label: string;
  missing: string;
}) {
  if (!href) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed px-3 py-1.5 text-xs text-muted-foreground"
        title={missing}
      >
        {label} — none
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium text-brand transition-colors hover:bg-muted"
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

const one = (r: SignedContractRef | null | undefined): SignedContractRef[] => (r ? [r] : []);

type LineItem = NonNullable<SignedContractDetail["contract_line_items"]>[number];
type Discount = NonNullable<SignedContractDetail["contract_discounts"]>[number];

/** A line references an item XOR a package — the name lives on whichever
 *  `*_detail` came back populated. */
const lineName = (li: LineItem): string =>
  li.item_detail?.name ?? li.package_detail?.name ?? "—";

const lineNameByUuid = (lines: LineItem[], uuid: string): string => {
  const hit = lines.find((l) => l.uuid === uuid);
  return hit ? lineName(hit) : "Line item";
};

/** Discounts carry no name or amount — the type and value are the description,
 *  and what they're worth depends on what they're applied to. */
function discountLabel(d: Discount): string {
  const t = d.discount_detail?.type;
  const v = d.discount_detail?.value;
  const amount =
    v == null ? "—" : t === "percentage" ? `${v}% off` : `${fmtCur2(v)} off`;
  const periods = d.duration_override ?? d.discount_detail?.duration_periods;
  return periods ? `${amount} · ${periods} periods` : amount;
}

const yesNo = (v: boolean | null | undefined): string =>
  v == null ? "—" : v ? "Yes" : "No";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </span>
      <div className="rounded-md border px-4 py-3">{children}</div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 sm:grid-cols-3">{children}</dl>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm tabular-nums">{children}</dd>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("tabular-nums", bold && "font-semibold")}>{value}</dd>
    </div>
  );
}

function VerdictButton({
  active,
  activeClass,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  activeClass: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50",
        active ? activeClass : "border-border text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
